import {
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useCallback, useState } from "react";
import {
  ListPaginationContext,
  Pagination,
  useListController,
  useLocaleState,
  useNotify,
  useTranslate,
} from "react-admin";

import { ROWS_PER_PAGE } from "../../constants";
import { DEVICE_ENROLLMENT_TOKENS_RESOURCE } from "../../constants/resources";
import {
  DeviceEnrollmentTokenStatus,
  RevokeDeviceEnrollmentTokenDocument,
  RevokeDeviceEnrollmentTokenMutation,
  RevokeDeviceEnrollmentTokenMutationVariables,
} from "../../generated/graphql";
import { useErrorHandler } from "../../hooks/useErrorHandler";
import { client } from "../../lib/apolloClient";
import { formatDateTimeInline } from "../../lib/dateUtils";
import {
  formatTokenUses,
  getScopeTypeLabel,
  getTokenStatusLabelKey,
  maskEnrollmentCode,
} from "../../lib/deviceEnrollment";
import { borderRadius, color, typography } from "../../theme/designTokens";
import type { DeviceEnrollmentTokenRecord } from "../../types/deviceEnrollment";
import { CustomConfirm } from "../CustomConfirm";

type Props = {
  organizationId: string | null;
  refreshKey?: number;
  onShow: (token: DeviceEnrollmentTokenRecord) => void;
};

const STATUS_STYLE: Record<DeviceEnrollmentTokenStatus, { color: string; bg: string }> = {
  [DeviceEnrollmentTokenStatus.Active]: { color: color.plannedText, bg: color.plannedChipBackground },
  [DeviceEnrollmentTokenStatus.Exhausted]: { color: color.gray, bg: color.background2 },
  [DeviceEnrollmentTokenStatus.Expired]: { color: color.gray, bg: color.background2 },
  [DeviceEnrollmentTokenStatus.Revoked]: { color: color.error, bg: color.background2 },
  [DeviceEnrollmentTokenStatus.ScopeInvalid]: { color: color.error, bg: color.background2 },
};

const headerCellSx = {
  backgroundColor: color.datagridHeaderBackground,
  fontSize: typography.fontSize.xs,
  fontWeight: typography.fontWeight.medium,
  color: color.typographyBase,
  borderBottom: "none",
};

export const DeviceEnrollmentTokenList = ({ organizationId, refreshKey = 0, onShow }: Props) => {
  const translate = useTranslate();
  const notify = useNotify();
  const handleError = useErrorHandler();
  const [locale] = useLocaleState();
  const [tokenToRevoke, setTokenToRevoke] = useState<DeviceEnrollmentTokenRecord | null>(null);
  const [isRevoking, setIsRevoking] = useState(false);

  const list = useListController<DeviceEnrollmentTokenRecord>({
    resource: DEVICE_ENROLLMENT_TOKENS_RESOURCE,
    filter: { organizationId, refreshKey },
    perPage: ROWS_PER_PAGE,
    sort: { field: "createdAt", order: "DESC" },
    disableSyncWithLocation: true,
    storeKey: false,
    queryOptions: { enabled: !!organizationId },
  });
  const { data: tokens = [], isLoading, refetch } = list;

  const handleRevoke = useCallback(async () => {
    if (!tokenToRevoke || isRevoking) return;
    setIsRevoking(true);
    try {
      await client.mutate<RevokeDeviceEnrollmentTokenMutation, RevokeDeviceEnrollmentTokenMutationVariables>({
        mutation: RevokeDeviceEnrollmentTokenDocument,
        variables: { id: tokenToRevoke.id },
      });
      notify("deviceEnrollment.revoke.success", { type: "success" });
      setTokenToRevoke(null);
      await refetch();
    } catch (error) {
      handleError(error, translate("deviceEnrollment.revoke.error"), { operation: "revokeDeviceEnrollmentToken" });
    } finally {
      setIsRevoking(false);
    }
  }, [handleError, isRevoking, notify, refetch, tokenToRevoke, translate]);

  if (!organizationId) {
    return (
      <Typography variant="body2" color={color.gray} data-testid="device-enrollment-tokens-no-organization">
        {translate("deviceEnrollment.section.noOrganization")}
      </Typography>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (tokens.length === 0) {
    return (
      <Typography variant="body2" color={color.gray} data-testid="device-enrollment-tokens-empty">
        {translate("deviceEnrollment.section.empty")}
      </Typography>
    );
  }

  return (
    <>
      <Box sx={{ backgroundColor: color.background, borderRadius: borderRadius.md, overflowX: "auto" }}>
        <Table size="small" data-testid="device-enrollment-tokens-table">
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellSx}>{translate("deviceEnrollment.column.code")}</TableCell>
              <TableCell sx={headerCellSx}>{translate("deviceEnrollment.column.scope")}</TableCell>
              <TableCell sx={headerCellSx}>{translate("deviceEnrollment.column.createdBy")}</TableCell>
              <TableCell sx={headerCellSx}>{translate("deviceEnrollment.column.expiresAt")}</TableCell>
              <TableCell sx={headerCellSx}>{translate("deviceEnrollment.column.uses")}</TableCell>
              <TableCell sx={headerCellSx}>{translate("deviceEnrollment.column.status")}</TableCell>
              <TableCell sx={headerCellSx} />
            </TableRow>
          </TableHead>
          <TableBody sx={{ backgroundColor: color.white }}>
            {tokens.map(token => {
              const statusStyle = STATUS_STYLE[token.status];
              const isActive = token.status === DeviceEnrollmentTokenStatus.Active;
              return (
                <TableRow key={token.id} data-testid={`device-enrollment-token-row-${token.id}`}>
                  <TableCell sx={{ fontFamily: "monospace", letterSpacing: "1px" }}>
                    {maskEnrollmentCode(null)}
                  </TableCell>
                  <TableCell>
                    {translate(getScopeTypeLabel(token.scopeType))}
                    {token.scopeLocation ? ` · ${token.scopeLocation.name}` : ""}
                  </TableCell>
                  <TableCell>{token.createdBy?.name ?? "-"}</TableCell>
                  <TableCell>{formatDateTimeInline(token.expiresAt, locale)}</TableCell>
                  <TableCell>{formatTokenUses(token)}</TableCell>
                  <TableCell>
                    <Box
                      component="span"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        px: 1.25,
                        height: 24,
                        borderRadius: 12,
                        fontSize: typography.fontSize.caption,
                        fontWeight: typography.fontWeight.medium,
                        color: statusStyle.color,
                        backgroundColor: statusStyle.bg,
                      }}
                    >
                      {translate(getTokenStatusLabelKey(token.status))}
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    <Button
                      size="small"
                      onClick={() => onShow(token)}
                      data-testid={`device-enrollment-token-show-${token.id}`}
                    >
                      {translate("deviceEnrollment.action.show")}
                    </Button>
                    {isActive && (
                      <Button
                        size="small"
                        onClick={() => setTokenToRevoke(token)}
                        data-testid={`device-enrollment-token-revoke-${token.id}`}
                      >
                        {translate("deviceEnrollment.action.revoke")}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <ListPaginationContext.Provider value={list}>
          <Pagination data-testid="device-enrollment-token-pagination" />
        </ListPaginationContext.Provider>
      </Box>

      <CustomConfirm
        isOpen={tokenToRevoke !== null}
        loading={isRevoking}
        title={translate("deviceEnrollment.revoke.title")}
        content={translate("deviceEnrollment.revoke.content")}
        onConfirm={handleRevoke}
        onClose={() => setTokenToRevoke(null)}
      />
    </>
  );
};
